<template>
    <div id="containerWeeklyHeatmap">
        <svg :style="{'width': svg.width, 'height': svg.height}">
            <!-- cumaltive yAxis -->
            <g>
                <line
                    :x1="svg.paddingL"
                    :x2="svg.width - svg.paddingR"
                    :y1="yCumaltiveScale(0)"
                    :y2="yCumaltiveScale(0)"
                    :stroke="themeColors.primary"
                    opacity="0.3"
                    style="stroke-width:0.5"/>
            </g>
            <!-- xAxis -->
            <g v-for="(x, xIdx) in weeks" :key="'x' + xIdx">
                <text
                    :y="svg.height - svg.paddingB + 17"
                    :x="xScale(x) + xScale.bandwidth()/2"
                    :fill="theme === 'light' ? themeColors.dark : themeColors.light"
                    text-anchor="middle"
                    font-size="12"
                    style="cursor:pointer"
                    @click="emitSelectWeek(x)"
                >
                    {{ __getOrdinal(x) }}
                </text>
                <line
                    :x1="xScale(x) + xScale.bandwidth()/2"
                    :x2="xScale(x) + xScale.bandwidth()/2"
                    :y1="0"
                    :y2="svg.height - svg.paddingB + 4"
                    :stroke="theme === 'light' ? themeColors.dark : themeColors.light"
                    opacity="0.2"
                    style="stroke-width:0.5"
                />
            </g>
            <!-- weeksums -->
            <transition-group name="list" appear tag="g">
                <template v-for="(week, weekIdx) in weekSums">
                    <g :key="'rect-' + weekIdx">
                        <line
                            v-if="weekSums[weekIdx + 1]"
                            :x1="xScale(week.week) + xScale.bandwidth()/2"
                            :x2="xScale(weekSums[weekIdx + 1].week) + xScale.bandwidth() / 2"
                            :y1="yCumaltiveScale(week.cost)"
                            :y2="yCumaltiveScale(weekSums[weekIdx + 1].cost)"
                            :stroke="themeColors.warning"
                            :stroke-width="2"
                            stroke-dasharray="7,1"
                            opacity="0.8"
                        >
                        </line>
                        <circle
                            :cx="xScale(week.week) + xScale.bandwidth()/2"
                            :cy="yCumaltiveScale(week.cost)"
                            :fill="themeColors.light"
                            :stroke="themeColors.warning"
                            r="4"
                            style="cursor:pointer"
                            @click="emitSelectWeek(week.week)"
                        >
                        </circle>
                        <text
                            :x="xScale(week.week) + xScale.bandwidth()/2"
                            :y="yCumaltiveScale(week.cost) - 10"
                            text-anchor="middle"
                            :fill="theme === 'light' ? themeColors.dark : themeColors.light"
                            font-size="11px"
                        >
                            {{ $_getDollar(week.cost).replace(' ','') }}
                        </text>
                    </g>
                </template>
            </transition-group>
            <rect
                :x="svg.paddingL"
                :y="svg.paddingT + (svg.height - svg.paddingT - svg.paddingB)/2"
                :width="svg.width - svg.paddingL - svg.paddingR"
                :height="(svg.height - svg.paddingT - svg.paddingB) / 2"
                :fill="theme === 'light' ? themeColors.primary : themeColors.primary"
                opacity="0.1"
            ></rect>
            <!-- bottom bars -->
            <template>
                <transition-group name="list" appear tag="g">
                    <template v-for="(item, itemIdx) in dataIfSelected">
                        <g :key="'line-' + itemIdx">
                            <line
                                :x1="xScale(item.week) + xScale.bandwidth() / 8 * item.day"
                                :x2="xScale(item.week) + xScale.bandwidth() / 8 * item.day"
                                :y1="yScale(item.cost)"
                                :y2="yScale(0)"
                                :stroke="themeColors.primary"
                                :opacity="item.cost == maxCost ? 0.7 : 0.3"
                                :stroke-width="xScale.bandwidth()/16"
                                @click="emitDetailLayer(item.day)"
                            ></line>
                            <text
                                v-if="item.cost == maxCost && maxCostOnlyOne"
                                class="shadow"
                                :x="xScale(item.week) + xScale.bandwidth()/8*item.day"
                                :y="yScale(item.cost) - 3"
                                :fill="theme === 'light' ? themeColors.dark : themeColors.light"
                                text-anchor="middle"
                                font-size="10"
                                opacity="0.7"
                            >
                                {{ $_getDollar(item.cost).replace(' ', '') }}
                            </text>
                        </g>
                    </template>
                </transition-group>
                <line
                    :x1="svg.paddingL"
                    :x2="svg.width - svg.paddingR"
                    :y1="yScale(0)"
                    :y2="yScale(0)"
                    :stroke="theme === 'light' ? themeColors.dark : themeColors.light"
                    opacity="0.1"
                    style="stroke-width:1"
                ></line>
            </template>

        </svg>
    </div>
</template>

<script>
import useCommonData from '@/composables/useCommonData'
import { inject, getCurrentInstance, computed, toRefs, onMounted, onUnmounted } from '@vue/composition-api'
export default {
    props: {
        svg: {
            type: Object,
            default: function () {
                return {}
            }
        },
        data: {
            type: Array,
            default: function () {
                return []
            }
        },
        weeks: {
            type: Array,
            default: function () {
                return []
            }
        },
        dataIfSelected: {
            type: Array,
            default: function () {
                return []
            }
        },
        weekSelected: {
            type: Number,
            default: null
        }
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const { svg, weeks, weekSelected, data, dataIfSelected } = toRefs(props)
        const themeColors = inject('themeColors')
        const theme = inject('theme')
        const { __getOrdinal } = useCommonData()
        const d3 = vm.$d3

        const weekSums = computed(() => {
            return weeks.value.map(week => {
                return {
                    week,
                    cost: data.value.filter(item => item.week === week).map(item => item.cost).reduce((a, b) => a + b, 0)
                }
            })
        })
        const weekSumCosts = computed(() => weekSums.value.map(item => item.cost))
        const minWeekSum = computed(() => Math.min(Math.min.apply(null, weekSumCosts.value), 0))
        const maxWeekSum = computed(() => Math.max(Math.max.apply(null, weekSumCosts.value), 0))
        const yCumaltiveScale = computed(() => {
            return d3.scaleLinear()
                .domain([Math.min(minWeekSum.value, 0), maxWeekSum.value]).nice()
                .range([svg.value.paddingT + (svg.value.height - svg.value.paddingT - svg.value.paddingB) / 2, svg.value.paddingT])
        })

        const dataIfSelectedCosts = computed(() => dataIfSelected.value.map(item => item.cost))
        const maxCost = computed(() => Math.max(Math.max.apply(null, dataIfSelectedCosts.value), 0))
        const minCost = computed(() => Math.min(Math.min.apply(null, dataIfSelectedCosts.value), 0))
        const maxCostOnlyOne = dataIfSelectedCosts.value.filter(cost => cost === maxCost.value).length === 1
        const xScale = computed(() => {
            return d3.scaleBand().domain(weeks.value)
                .range([svg.value.paddingL, svg.value.width - svg.value.paddingR]).padding([(weekSelected.value ? 0 : 0.1)])
        })
        const yScale = computed(() => {
            return d3.scaleLinear()
                .domain([Math.min(minCost.value, 0), maxCost.value]).nice()
                .range([svg.value.height - svg.value.paddingB - 13, svg.value.paddingT + (svg.value.height - svg.value.paddingT - svg.value.paddingB) / 2 + 13])
        })
        const emitHandleResize = () => context.emit('resize:svg')
        const emitDetailLayer = date => context.emit('detail-layer', date)
        const emitSelectWeek = week => context.emit('select:week', week)
        onMounted(() => {
            window.addEventListener('resize', emitHandleResize)
            vm.$nextTick(() => {
                emitHandleResize()
            })
        })
        onUnmounted(() => {
            window.removeEventListener('resize', emitHandleResize)
        })
        return {
            maxCostOnlyOne,
            themeColors,
            theme,
            __getOrdinal,
            xScale,
            yScale,
            maxCost,
            weekSums,
            yCumaltiveScale,
            emitDetailLayer,
            emitSelectWeek
        }
    }
}
</script>

<style>

</style>
